// Phase 4c — shared Zod arg fragments for dashboard tile / sub-form
// schemas. SSOT for the recurring kwarg shapes so every tile and
// sub-form validates `:span` and `(col …)` slots byte-identically.
//
// Symbol-or-string acceptance for plain `z.string()` / `z.enum(...)`
// kwargs is handled centrally by `relaxSymbols` at the `parseOpCall`
// chokepoint (see schema/sym-codec.ts) — tiles/sub-forms write vanilla
// Zod; no per-fragment helper is needed here.
//
// Parser shapes (see normalize.ts): a vector `:span [4 2]` arrives as a
// JS number array; a `(col …)` ref as a raw OpCall AST node.

import { z } from 'zod';

import { SPAN_RANGE } from './value.ts';

/**
 * `:span [W H]` — a two-int vector, each component in the fixed
 * 12-column grid range. Output is a readonly tuple matching `Span`.
 */
const spanInt = z.number().int().min(SPAN_RANGE.min).max(SPAN_RANGE.max);
export const spanArg: z.ZodType<readonly [number, number]> = z
  .tuple([spanInt, spanInt])
  .transform(([w, h]) => [w, h] as const)
  .describe(`[width height] grid span, each ${SPAN_RANGE.min}–${SPAN_RANGE.max} columns of the 12-column grid`);

/**
 * A deferred column-ref arg (`:x (col w t)`, `:value (col w v)`). The
 * raw `(col …)` OpCall is preserved unevaluated here; the tile /
 * sub-form `apply` evaluates it via `ctx.evalNode` and narrows the
 * result with `expectColumnRef`. Typed `unknown` because the validated
 * shape is an AST node, not a resolved value.
 */
export const colRefArg = z.unknown().describe('a (col <dataset> <column> [path…]) column reference');
